'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  Wrench,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Bell,
  Clock,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const navGroups = [
  {
    label: 'Operations',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', href: '/booking-management-dashboard', badge: null },
      { icon: CalendarDays, label: 'Bookings', href: '/booking-management-dashboard', badge: '3' },
      { icon: Clock, label: 'Schedule', href: '/booking-management-dashboard', badge: null },
    ],
  },
  {
    label: 'Management',
    items: [
      { icon: Users, label: 'Customers', href: '/booking-management-dashboard', badge: null },
      { icon: Wrench, label: 'Services', href: '/booking-management-dashboard', badge: null },
      { icon: BarChart3, label: 'Reports', href: '/booking-management-dashboard', badge: null },
    ],
  },
  {
    label: 'System',
    items: [
      { icon: Bell, label: 'Notifications', href: '/booking-management-dashboard', badge: '5' },
      { icon: Settings, label: 'Settings', href: '/booking-management-dashboard', badge: null },
    ],
  },
];

interface DashboardSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function DashboardSidebar({ collapsed, onToggle }: DashboardSidebarProps) {
  const pathname = usePathname();

  return (
    <aside
      className="flex flex-col bg-card border-r border-border h-screen sticky top-0 z-40 transition-all duration-300 ease-in-out flex-shrink-0"
      style={{ width: collapsed ? 64 : 240 }}
    >
      {/* Header */}
      <div className="h-16 flex items-center justify-between px-3 border-b border-border flex-shrink-0">
        {!collapsed && (
          <Link href="/" className="flex items-center gap-2 overflow-hidden">
            <AppLogo size={30} />
            <span className="font-extrabold text-base tracking-tight text-foreground whitespace-nowrap">
              Detail<span className="text-primary">Book</span>
            </span>
          </Link>
        )}
        {collapsed && (
          <div className="mx-auto">
            <AppLogo size={30} />
          </div>
        )}
        {!collapsed && (
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft size={16} />
          </button>
        )}
      </div>

      {/* Collapsed expand button */}
      {collapsed && (
        <button
          onClick={onToggle}
          className="mt-2 mx-auto p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          aria-label="Expand sidebar"
        >
          <ChevronRight size={16} />
        </button>
      )}

      {/* Nav Groups */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 flex flex-col gap-4">
        {navGroups.map((group) => (
          <div key={`nav-group-${group.label}`}>
            {!collapsed && (
              <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground px-3 mb-1.5">
                {group.label}
              </p>
            )}
            <div className="flex flex-col gap-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href;
                return (
                  <Link
                    key={`sidebar-${item.label}`}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`sidebar-nav-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`}
                  >
                    <span className="relative flex-shrink-0">
                      <Icon size={18} />
                      {item.badge && collapsed && (
                        <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[9px] font-700 rounded-full w-3.5 h-3.5 flex items-center justify-center">
                          {item.badge}
                        </span>
                      )}
                    </span>
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge && (
                          <span className="bg-accent text-accent-foreground text-[10px] font-700 rounded-full px-1.5 py-0.5 flex-shrink-0">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User Footer */}
      <div className="border-t border-border p-3 flex-shrink-0">
        {collapsed ? (
          <button
            className="w-full flex justify-center p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut size={18} />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-700 flex-shrink-0">
              MR
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-600 text-foreground truncate">Marcus Rivera</p>
              <p className="text-xs text-muted-foreground truncate">Owner</p>
            </div>
            <button
              className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={15} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}